import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MuiButton from '@mui/material/Button';
import LandingLayout from './components/LandingLayout';
import { NEARPOD_PUBLISHER, NearpodLesson } from './data/nearpod-publisher';
import PressoLessonPreview from './PressoLessonPreview';
import { CurriculumLesson } from './data/peninsula-district';
import TestimonialsSection from './components/TestimonialsSection';
import TESTIMONIALS from './data/testimonials';

const SHADOW = '4px 4px 0px #1e1e1e';
const SHADOW_SM = '2px 2px 0px #1e1e1e';
const PRESSTO_PURPLE = '#5C4FB8';
const NEARPOD_BLUE = '#16A8F9';
const NEARPOD_DARK = '#0D1B2A';
const CREAM = '#F2EEE5';

const PLAN_LABELS: Record<string, string> = {
  'op-ed': 'Opinion', 'opinion': 'Opinion', 'sequence': 'Sequence',
  'cause-effect': 'Cause & Effect', 'compare-contrast': 'Compare & Contrast',
  'retell-the-narrative': 'Retell', 'personal-narrative': 'Personal Narrative',
  'description': 'Description', 'problem-solution': 'Problem & Solution', 'basic': 'Basic',
};

const PLAN_COLORS: Record<string, string> = {
  'opinion': '#E31D23', 'op-ed': '#E31D23',
  'cause-effect': '#F97316',
  'compare-contrast': '#7C3AED',
  'sequence': '#0D9488',
  'description': '#2563EB',
  'problem-solution': '#DC2626',
  'retell-the-narrative': '#B45309',
  'personal-narrative': '#DB2777',
};

const AREA_ICONS: Record<string, string> = {
  'ELA': '✍️',
  'Science': '🔬',
  'Social Studies': '🌍',
  'Math': '🔢',
};

const VALUE_PROPS = [
  {
    icon: '🎯',
    title: 'Interactive lesson, then the write',
    body: 'Nearpod gets students engaged and thinking. Pressto captures what they learned — students move from the interactive lesson directly into structured writing, while ideas are fresh.',
  },
  {
    icon: '🧩',
    title: 'Writing plans matched to content',
    body: 'Every Pressto activity uses the structure that fits — cause & effect for science, retell for history, opinion for civics. No blank page, no guessing what to write.',
  },
  {
    icon: '✨',
    title: 'AI Writing Buddy',
    body: 'Asks questions, prompts thinking, suggests words — but never writes for students. The work stays theirs, and teachers see how it got there.',
  },
  {
    icon: '📰',
    title: 'Publish to a class newspaper',
    body: "Students' best Nearpod-paired writing becomes part of a real class or school newspaper — giving the lesson a lasting, shareable output.",
  },
  {
    icon: '🔍',
    title: 'Behavioral AI detection',
    body: 'Detects AI-generated writing through typing patterns and revision history — not just the text. Teachers know what actually happened, not just what was submitted.',
  },
  {
    icon: '🏫',
    title: 'Built for real classrooms',
    body: 'Google Classroom rostering, co-teacher access, per-student progress monitoring. Drop Pressto into the Nearpod workflow without adding friction.',
  },
];

function CTAButton({ children, onClick, dark = true }: { children: React.ReactNode; onClick: () => void; dark?: boolean }) {
  return (
    <MuiButton
      variant="contained"
      onClick={onClick}
      sx={{
        backgroundColor: dark ? NEARPOD_DARK : NEARPOD_BLUE,
        color: '#fff',
        fontFamily: 'Montserrat, Inter, sans-serif', fontWeight: 700, fontSize: '1rem',
        textTransform: 'none', borderRadius: '6px', border: '2px solid #1e1e1e',
        boxShadow: SHADOW, px: 4, py: 1.5,
        '&:hover': { boxShadow: SHADOW_SM, transform: 'translate(2px,2px)' },
        transition: 'all 100ms',
      }}
    >
      {children}
    </MuiButton>
  );
}

function Chip({ label, bg, color }: { label: string; bg: string; color: string }) {
  return (
    <Box sx={{
      backgroundColor: bg, color, borderRadius: '4px', px: 0.75, py: 0.3,
      fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '0.6rem',
      textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap',
    }}>{label}</Box>
  );
}

function LessonCard({ lesson, onClick }: { lesson: NearpodLesson; onClick: () => void }) {
  const planColor = PLAN_COLORS[lesson.plan] || NEARPOD_BLUE;
  return (
    <Box
      onClick={onClick}
      sx={{
        borderRadius: '8px', border: '2px solid #1e1e1e',
        boxShadow: SHADOW_SM, backgroundColor: '#fff', overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
        cursor: 'pointer',
        '&:hover': { boxShadow: '2px 2px 0px #1e1e1e', transform: 'translate(2px,2px)' },
        transition: 'all 100ms',
      }}
    >
      <Box sx={{ height: 6, backgroundColor: NEARPOD_BLUE }} />
      <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', gap: 0.75, mb: 1, flexWrap: 'wrap' }}>
          <Chip label={lesson.grade} bg={NEARPOD_DARK} color="#fff" />
          {PLAN_LABELS[lesson.plan] && (
            <Chip label={PLAN_LABELS[lesson.plan]} bg={planColor} color="#fff" />
          )}
        </Box>
        <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '0.85rem', color: '#1e1e1e', mb: 0.5, lineHeight: 1.2 }}>
          {lesson.title}
        </Typography>
        <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.72rem', color: '#888', mb: 0.75, fontStyle: 'italic' }}>
          Pairs with: {lesson.nearpodTitle.replace(/:\s*[\d\-K]+.*$/, '').trim()}
        </Typography>
        <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.8rem', color: '#666', lineHeight: 1.5, flex: 1, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {lesson.prompt}
        </Typography>
        <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '0.7rem', color: NEARPOD_BLUE, mt: 1.25, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Preview →
        </Typography>
      </Box>
    </Box>
  );
}

export default function NearpodPublisherPage() {
  const navigate = useNavigate();
  const publisher = NEARPOD_PUBLISHER;
  const [preview, setPreview] = useState<NearpodLesson | null>(null);

  const byArea = useMemo(() => {
    const map: Record<string, NearpodLesson[]> = {};
    publisher.lessons.forEach(l => {
      (map[l.area] = map[l.area] || []).push(l);
    });
    return map;
  }, [publisher]);

  return (
    <LandingLayout>
      {/* Hero */}
      <Box sx={{ backgroundColor: CREAM, borderBottom: '2px solid #1e1e1e', py: { xs: 8, md: 12 }, px: { xs: 3, md: 6 } }}>
        <Box sx={{ maxWidth: 880, mx: 'auto', textAlign: 'center' }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5, backgroundColor: '#fff', border: '2px solid #1e1e1e', borderRadius: '6px', px: 2, py: 1, mb: 4, boxShadow: SHADOW_SM }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: NEARPOD_BLUE }} />
            <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Publisher Partnership · Grades {publisher.gradeRange}
            </Typography>
          </Box>

          <Typography variant="h1" sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: { xs: '2.2rem', md: '3.2rem' }, lineHeight: 1.1, color: '#2C2C2C', mb: 2 }}>
            <Box component="span" sx={{ color: PRESSTO_PURPLE }}>Pressto</Box>
            {' + '}
            <Box component="span" sx={{ color: NEARPOD_BLUE }}>Nearpod</Box>
            <Box component="span" sx={{ color: NEARPOD_DARK }}>.</Box>
          </Typography>

          <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: { xs: '1.1rem', md: '1.3rem' }, color: '#444', lineHeight: 1.6, mb: 2, fontWeight: 600 }}>
            Nearpod delivers the interactive lesson.
          </Typography>
          <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: { xs: '1.1rem', md: '1.3rem' }, color: PRESSTO_PURPLE, lineHeight: 1.6, mb: 4, fontWeight: 700 }}>
            Pressto is the writing layer for Nearpod.
          </Typography>

          <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '1rem', color: '#666', lineHeight: 1.6, mb: 5, maxWidth: 640, mx: 'auto' }}>
            {publisher.lessonCount}+ ready-to-use writing activities, one for every Nearpod lesson — across ELA, Science, and Social Studies for grades {publisher.gradeRange}.
            Students engage with Nearpod, then write in Pressto while the content is still alive.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <CTAButton onClick={() => navigate('/contact')}>Let&apos;s partner →</CTAButton>
            <CTAButton dark={false} onClick={() => navigate('/register')}>Try Pressto free</CTAButton>
          </Box>
        </Box>
      </Box>

      {/* Value props */}
      <Box sx={{ backgroundColor: '#fff', py: { xs: 6, md: 9 }, px: { xs: 3, md: 6 } }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
          <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '1.6rem', color: '#1e1e1e', mb: 1.5, textAlign: 'center' }}>
            The interactive lesson needs a writing layer
          </Typography>
          <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '1rem', color: '#666', lineHeight: 1.6, mb: 5, textAlign: 'center', maxWidth: 700, mx: 'auto' }}>
            Nearpod builds knowledge and engagement. Pressto turns that engagement into writing — structured, scaffolded, and worth an audience.
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 3 }}>
            {VALUE_PROPS.map(f => (
              <Box key={f.title} sx={{ border: '2px solid #1e1e1e', borderRadius: '8px', boxShadow: SHADOW_SM, p: 3, display: 'flex', gap: 2 }}>
                <Box component="span" sx={{ fontSize: '1.6rem', lineHeight: 1 }}>{f.icon}</Box>
                <Box>
                  <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '1rem', color: '#1e1e1e', mb: 0.5 }}>{f.title}</Typography>
                  <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.9rem', color: '#555', lineHeight: 1.5 }}>{f.body}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Stats bar */}
      <Box sx={{ backgroundColor: '#E8F5FE', borderTop: '2px solid #1e1e1e', borderBottom: '2px solid #1e1e1e', py: 4, px: { xs: 3, md: 6 } }}>
        <Box sx={{ maxWidth: 900, mx: 'auto', display: 'flex', justifyContent: 'center', gap: { xs: 4, md: 8 }, flexWrap: 'wrap' }}>
          {[
            { n: publisher.lessonCount, label: 'paired writing activities' },
            { n: publisher.areaCount, label: 'subject areas' },
            { n: publisher.gradeRange, label: 'grade range', isText: true },
          ].map(s => (
            <Box key={s.label} sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '2.4rem', color: NEARPOD_BLUE, lineHeight: 1 }}>
                {s.isText ? s.n : `${s.n}+`}
              </Typography>
              <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.85rem', color: '#666', mt: 0.5 }}>
                {s.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Lesson cards by area */}
      <Box sx={{ backgroundColor: '#F7F8FA', py: { xs: 6, md: 9 }, px: { xs: 3, md: 6 } }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '1.5rem', color: '#1e1e1e', mb: 1, textAlign: 'center' }}>
            One writing activity per Nearpod lesson
          </Typography>
          <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.9rem', color: '#888', mb: 5, textAlign: 'center' }}>
            Each card pairs with a real Nearpod lesson — students learn in Nearpod, then write in Pressto.
          </Typography>
          {publisher.areas.filter(area => byArea[area]?.length).map(area => (
            <Box key={area} sx={{ mb: 6 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '2px', backgroundColor: NEARPOD_BLUE, flexShrink: 0 }} />
                <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '1.1rem', color: '#1e1e1e' }}>
                  {AREA_ICONS[area]} {area}
                </Typography>
                <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.8rem', color: '#aaa', ml: 0.5 }}>
                  {byArea[area].length} {byArea[area].length === 1 ? 'activity' : 'activities'}
                </Typography>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
                {byArea[area].map((lesson, i) => (
                  <LessonCard key={i} lesson={lesson} onClick={() => setPreview(lesson)} />
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Testimonials */}
      <TestimonialsSection
        testimonials={TESTIMONIALS.slice(0, 3)}
        title="What educators are saying"
      />

      {/* Bottom CTA */}
      <Box sx={{ backgroundColor: NEARPOD_DARK, borderTop: '2px solid #1e1e1e', py: { xs: 8, md: 10 }, px: 3, textAlign: 'center' }}>
        <Box sx={{ maxWidth: 640, mx: 'auto' }}>
          <Typography variant="h2" sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: { xs: '1.7rem', md: '2.4rem' }, color: '#fff', mb: 2, lineHeight: 1.15 }}>
            Let&apos;s make every Nearpod lesson<br />end with great writing.
          </Typography>
          <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '1.05rem', color: 'rgba(255,255,255,0.7)', mb: 5 }}>
            Nearpod&apos;s interactive lessons + Pressto&apos;s writing workspace = a K-8 program where every student engages, writes, and publishes.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <CTAButton dark={false} onClick={() => navigate('/contact')}>Get in touch →</CTAButton>
            <MuiButton
              variant="outlined"
              onClick={() => navigate('/register')}
              sx={{
                color: '#fff', borderColor: 'rgba(255,255,255,0.4)',
                fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '1rem',
                textTransform: 'none', borderRadius: '6px', px: 4, py: 1.5,
                '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.08)' },
              }}
            >
              Try Pressto free
            </MuiButton>
          </Box>
        </Box>
      </Box>

      {preview && (
        <PressoLessonPreview
          lesson={preview as unknown as CurriculumLesson}
          onClose={() => setPreview(null)}
          onRegister={() => navigate('/register')}
          onPricing={() => navigate('/contact')}
        />
      )}
    </LandingLayout>
  );
}
